import React, { memo, forwardRef, useImperativeHandle, useState, useCallback, useEffect, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";

import api from "services/api";
import pathnames from "routes/pathnames";
import classNames from "common/class-names";
import serveRequestErrors from "common/serve-request-errors";
import { formatDatePattern, formatDateTime } from "common/calendar";
import AppButton from "components/app-button";
import AppStatus from "components/app-status";
import AppCloseIcon from "components/icons/app-close-icon";
import editIcon from "assets/images/components/pages/user-management/edit-icon.svg";

const TABS = [
	{ label: "Personal Info", value: "PERSONAL" },
	{ label: "Employee Info", value: "EMPLOYEE" },
];

export const AppEmployeeModal = (props, ref) => {
	const navigate = useNavigate();
	const [data, setData] = useState({ personalInfo: {}, employeeInfo: {} });
	const [visible, setVisible] = useState(false);
	const [tab, setTab] = useState(TABS[0].value);

	//prettier-ignore
	const tabClassName = useCallback((currentTab) => {
		return classNames({
			"employee-modal__tab": true,
			"employee-modal__tab--active": tab === currentTab,
		});
	}, [tab]);

	const onHandleShow = useCallback((user) => {
		setTab(TABS[0].value);

		setData((prev) => ({ ...prev, ...user }));

		setVisible(true);
	}, []);

	const onHandleDismiss = useCallback(() => {
		setData({ personalInfo: {}, employeeInfo: {} });
		setVisible(false);
	}, []);

	const onHandleTabChange = useCallback((index) => {
		setTab(index);
	}, []);

	//prettier-ignore
	const onHandleEditProfile = useCallback(() => {
		navigate(pathnames.userManagement.employee + data.email);
	}, [navigate, data]);

	//prettier-ignore
	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	useEffect(() => {
		const onHandleGetDetails = async () => {
			let personalInfo = null;
			let employeeInfo = null;

			try {
				personalInfo = await api.get.employee.personalInfo({ email: data.email });
				employeeInfo = await api.get.employee.employeeInfo({ email: data.email });
			} catch (error) {
				serveRequestErrors(error);
			}
			if (personalInfo && employeeInfo) {
				setData((prev) => ({ ...prev, personalInfo, employeeInfo }));
			}
		};

		if (data.email) onHandleGetDetails();
	}, [data.email]);

	const residentialAddress = useMemo(() => {
		let address = [];

		if (!data?.personalInfo?.contactInfo) return "";

		address.push(data.personalInfo?.contactInfo?.correspondenceAddress1);

		if (data.personalInfo?.contactInfo?.correspondenceAddress2) address.push(data.personalInfo?.contactInfo?.correspondenceAddress2);

		address.push(data.personalInfo?.contactInfo?.correspondencePostcode);

		address.push(data.personalInfo?.contactInfo?.correspondenceState);

		address.push(data.personalInfo?.contactInfo?.correspondenceRegion);

		return address.filter((o) => o).join(", ");
	}, [data.personalInfo?.contactInfo]);

	const permenantAddress = useMemo(() => {
		let address = [];

		if (!data?.personalInfo?.contactInfo) return "";

		address.push(data.personalInfo?.contactInfo?.residentialAddress1);

		if (data.personalInfo?.contactInfo?.residentialAddress2) address.push(data.personalInfo?.contactInfo?.residentialAddress2);

		address.push(data.personalInfo?.contactInfo?.residentialPostcode);

		address.push(data.personalInfo?.contactInfo?.residentialRegion);

		address.push(data.personalInfo?.contactInfo?.residentialState);

		return address.filter((o) => o).join(", ");
	}, [data.personalInfo?.contactInfo]);

	const tabsInfo = useMemo(() => {
		const info = {
			employee: {
				basic: {
					section: "",
					list: [
						{ label: "Organisation", value: data.employeeInfo?.organisationName },
						{ label: "Work Email", value: data.employeeInfo?.workEmail },
						{ label: "Position", value: data.employeeInfo?.position },
						{ label: "Level", value: data.employeeInfo?.level },
						{ label: "Contract Type", value: data.employeeInfo?.contractTypeDesc },
						{ label: "Join Date", value: formatDatePattern(data.employeeInfo?.dateJoined) },
						{ label: "Probation End Date", value: formatDatePattern(data.employeeInfo?.probationEndDate) },
						{ label: "Latest Promotion Date", value: formatDatePattern(data.employeeInfo?.lastPromotionDate) },
						{ label: "Internship End Date", value: formatDatePattern(data.employeeInfo?.internshipEndDate) },
						{ label: "Reporting Manager", value: data.employeeInfo?.reportingManagerName },
						{ label: "Region Calendar", value: data.employeeInfo?.holidayRegionCode },
					],
				},
				benefits: {
					section: "Benefits Enrollment",
					list: [
						{ label: "Benefit Plan", value: data.employeeInfo?.benefitPackageName },
					],
				},
			},
			personal: {
				basic: {
					section: "",
					list: [
						{ label: "Full Name", value: data.personalInfo?.general?.name },
						{ label: "Identification Type", value: data.personalInfo?.general?.identificationTypeDesc },
						{ label: "NRIC No.", value: data.personalInfo?.general?.nric },
						{ label: "Passport No.", value: data.personalInfo?.general?.passport },
						{ label: "Date of Birth", value: formatDatePattern(data.personalInfo?.general?.dateOfBirth) },
						{ label: "Gender", value: data.personalInfo?.general?.genderDesc },
						{ label: "Nationality", value: data.personalInfo?.general?.nationalityDesc },
						{ label: "Marital Status", value: data.personalInfo?.general?.maritalStatusDesc },
					],
				},
				contactInfo: {
					section: "Contact Info",
					list: [
						{ label: "Mobile No.", value: data.personalInfo?.contactInfo?.fullMobileNo },
						{ label: "Personal Email", value: data.personalInfo?.contactInfo?.email },
						{ label: "Residential Address", value: residentialAddress },
						{ label: "Permanent Address", value: data.personalInfo?.contactInfo?.sameAsResidentialAddress === "Y" ? "Same as Correspondence Address" : permenantAddress },
					],
				},
				emergencyContact: {
					section: "Emergency Contact",
					list: [
						{ label: "Full Name", value: data.personalInfo?.emergencyContact?.fullName },
						{ label: "Relationship", value: data.personalInfo?.emergencyContact?.relationshipDesc },
						{ label: "Mobile No.", value: data.personalInfo?.emergencyContact?.fullMobileNo },
					],
				},
				emergencyContactOptional: {
					section: "Emergency Contact (Optional)",
					list: [
						{ label: "Full Name", value: data.personalInfo?.emergencyContact?.fullName2 },
						{ label: "Relationship", value: data.personalInfo?.emergencyContact?.relationshipDesc2 },
						{ label: "Mobile No.", value: data.personalInfo?.emergencyContact?.fullMobileNo2 },
					],
				},
				healthDeclaration: {
					section: "Health Declaration & Consent",
					list: [
						{
							label: "Dietary Preferences",
							value: data.personalInfo?.healthDeclaration?.dietaryPreferences
								?.map(pref => pref.dietaryOption === "OTHER" ? `Others (${pref.description.charAt(0).toUpperCase() + pref.description.slice(1).toLowerCase()})` : pref.dietaryOption.charAt(0).toUpperCase() + pref.dietaryOption.slice(1).toLowerCase())
								.join(", ") || null
						},
						{ label: "Food Allergy", value: data.personalInfo?.healthDeclaration?.foodAllergy ? data.personalInfo?.healthDeclaration?.foodAllergyDetails : null },
						{ label: "Medical Condition", value: data.personalInfo?.healthDeclaration?.medicalCondition ? data.personalInfo?.healthDeclaration?.medicalConditionDetails : null },
						{ label: "Long Term Medication", value: data.personalInfo?.healthDeclaration?.longTermMedication ? data.personalInfo?.healthDeclaration?.longTermMedicationDetails : null },
						{ label: "Info Confirmed", value: data.personalInfo?.healthDeclaration?.infoConfirmed ? "Yes" : "No" },
						{ label: "Acknowledged", value: data.personalInfo?.healthDeclaration?.acknowledgePolicy ? "Yes" : "No" }
					]
				},
				bankDetails: {
					section: "Bank Details",
					list: [
						{ label: "Bank Name", value: data.personalInfo?.bank?.bankNameDesc },
						{ label: "Bank Account No.", value: data.personalInfo?.bank?.bankAccountNo },
						{ label: "Swift Code / Branch Name", value: data.personalInfo?.bank?.swiftCode },
					],
				},
				contributions: {
					section: "Contributions",
					list: [
						{ label: "EPF No.", value: data.personalInfo?.contribution?.epfNo },
						{ label: "Income Tax No.", value: data.personalInfo?.contribution?.incomeTaxNo },
						{ label: "Socso No.", value: data.personalInfo?.contribution?.socsoNo },
					],
				},
			},
		};

		return info;
	}, [residentialAddress, permenantAddress, data.employeeInfo, data.personalInfo]);

	const Tabs = useCallback(() => {
		const type = tab.toLowerCase();
		const object = Object.keys(tabsInfo[type]);

		if (!object.length) return null;

		return (
			<div className="employee-modal__body">
				<div className="employee-modal__container">
					{object.map((a) => {
						const data = tabsInfo[type][a];

						return (
							<Fragment key={a}>
								{data.section && <p className="employee-modal__title">{data.section}</p>}
								<div className="employee-modal__wrapper">
									{data.list.map((b) => {
										return (
											<div className="employee-modal__info" key={b.label}>
												<p className="employee-modal__label">{b.label}</p>
												<p className="employee-modal__value">{b.value || "-"}</p>
											</div>
										);
									})}
								</div>
							</Fragment>
						);
					})}
				</div>
			</div>
		);
	}, [tabsInfo, tab]);

	return (
		<Modal classes={{ root: "app-employee-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="employee-modal" aria-describedby="employee-profile-modal">
			<div className="employee-modal">
				<div className="employee-modal__main">
					<button className="employee-modal__close-button" onClick={onHandleDismiss}>
						<AppCloseIcon color="#666666" />
					</button>

					<div className="employee-modal__header">
						<div className="employee-modal__image">
							{data.personalInfo?.avatar ? <img src={data.personalInfo.avatar} alt="profile" /> : <p className="employee-modal__initial">{data.personalInfo?.general?.name?.trim()?.[0]?.toUpperCase() || "E"}</p>}
						</div>
						<div className="employee-modal__profile">
							<p className="employee-modal__title">{data.personalInfo?.general?.name}</p>
							<p className="employee-modal__text">
								Last updated by: {data.lastModifiedByName}, {formatDateTime(new Date(data.lastModifiedDate))}
							</p>
						</div>

						<AppButton type="button" label="Edit" icon={editIcon} onClick={onHandleEditProfile} />
					</div>

					<div className="info">
						<div className="info__box">
							<p className="employee-modal__text">Employee ID</p>
							<p className="employee-modal__title">{data.employeeInfo?.employeeId}</p>
						</div>
						<div className="info__box">
							<p className="employee-modal__text">Status</p>
							<div className="employee-modal__title">
								<AppStatus status={data.employeeInfo?.employmentStatus} />
							</div>
						</div>
					</div>

					<div className="employee-modal__tabs">
						{TABS.map((o) => (
							<button key={o.value} className={tabClassName(o.value)} onClick={() => onHandleTabChange(o.value)}>
								{o.label}
							</button>
						))}
					</div>

					<Tabs />
				</div>
			</div>
		</Modal>
	);
};

export default memo(forwardRef(AppEmployeeModal));

AppEmployeeModal.propTypes = {
	ref: PropTypes.object,
};
